import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, X, Maximize2 } from 'lucide-react';
import AdminChat from './AdminChat';
import './AdminChat.css';

const BUBBLE_STORAGE_KEY = 'wnc_admin_chat_bubble_pos';
const DRAWER_MARGIN = 12;

/**
 * Floating bubble that lives on every admin page.
 * - Click: toggle a drawer that floats near the bubble.
 * - Drag: reposition the bubble; position persists in localStorage.
 * - Also exposes a "maximize" button that navigates to the full-page /admin/assistant.
 */
const AdminChatBubble = () => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(() => {
    try { return JSON.parse(localStorage.getItem(BUBBLE_STORAGE_KEY)) || null; } catch { return null; }
  });
  const [dragging, setDragging] = useState(false);
  const dragState = useRef({ moved: false, startX: 0, startY: 0, offX: 0, offY: 0 });
  const bubbleRef = useRef(null);

  useEffect(() => {
    // Default bottom-right position on first paint
    if (!pos) {
      setPos({ x: window.innerWidth - 76, y: window.innerHeight - 76 });
    }
  }, [pos]);

  useEffect(() => {
    if (pos) localStorage.setItem(BUBBLE_STORAGE_KEY, JSON.stringify(pos));
  }, [pos]);

  const onPointerDown = (e) => {
    if (!bubbleRef.current) return;
    const rect = bubbleRef.current.getBoundingClientRect();
    dragState.current = {
      moved: false,
      startX: e.clientX,
      startY: e.clientY,
      offX: e.clientX - rect.left,
      offY: e.clientY - rect.top,
    };
    setDragging(true);
    bubbleRef.current.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!dragging) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragState.current.moved = true;
    const size = 56;
    const nx = Math.max(6, Math.min(window.innerWidth - size - 6, e.clientX - dragState.current.offX));
    const ny = Math.max(6, Math.min(window.innerHeight - size - 6, e.clientY - dragState.current.offY));
    setPos({ x: nx, y: ny });
  };

  const onPointerUp = (e) => {
    if (!dragging) return;
    setDragging(false);
    try { bubbleRef.current?.releasePointerCapture(e.pointerId); } catch { /* pointer already released */ }
    if (!dragState.current.moved) setOpen(v => !v);
  };

  // Compute drawer position relative to bubble
  const drawerStyle = () => {
    if (!pos) return {};
    const drawerW = Math.min(420, window.innerWidth - 24);
    const drawerH = Math.min(620, window.innerHeight - 100);
    // Anchor drawer to the side of the bubble that has more room
    let left = pos.x - drawerW - DRAWER_MARGIN;
    if (left < DRAWER_MARGIN) left = pos.x + 64 + DRAWER_MARGIN;
    if (left + drawerW > window.innerWidth - DRAWER_MARGIN) left = window.innerWidth - drawerW - DRAWER_MARGIN;
    let top = pos.y - drawerH + 40;
    if (top < DRAWER_MARGIN) top = DRAWER_MARGIN;
    if (top + drawerH > window.innerHeight - DRAWER_MARGIN) top = window.innerHeight - drawerH - DRAWER_MARGIN;
    return { left, top };
  };

  const goFullscreen = () => {
    setOpen(false);
    window.location.href = '/admin/assistant';
  };

  if (!pos) return null;

  return (
    <>
      <div
        ref={bubbleRef}
        className={`admin-chat-bubble ${dragging ? 'dragging' : ''}`}
        style={{ left: pos.x, top: pos.y }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        title="WNC Admin Assistant — click to open, drag to reposition"
        data-testid="admin-chat-bubble"
      >
        <Sparkles className="w-6 h-6" />
      </div>

      {open && (
        <div className="admin-chat-drawer-overlay" style={drawerStyle()} data-testid="admin-chat-drawer">
          <div className="admin-chat-drawer-header">
            <div className="admin-chat-drawer-title">
              <Sparkles className="w-3.5 h-3.5" /> WNC Admin Assistant
            </div>
            <div className="admin-chat-drawer-actions">
              <button onClick={goFullscreen} className="admin-chat-drawer-btn" title="Open full page"><Maximize2 className="w-3.5 h-3.5" /></button>
              <button onClick={() => setOpen(false)} className="admin-chat-drawer-btn" title="Close"><X className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="admin-chat-drawer-body">
            <AdminChat compact />
          </div>
        </div>
      )}
    </>
  );
};

export default AdminChatBubble;
